import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Globe } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useBlogTranslation } from "../translations";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_teams-integration/artifacts/bfkk1953_Cayus%20Square%20Logo%20%28150%20x%2050%20px%29.png";

const LANG_LABELS = { en: "English", fr: "Français", es: "Español" };

const BlogListPage = () => {
  const { language, switchLanguage } = useLanguage();
  const t = useBlogTranslation(language);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog/articles`)
      .then((res) => setArticles(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getField = (article, field) => {
    return article[`${field}_${language}`] || article[`${field}_fr`] || article[`${field}_en`] || "";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString(language === "fr" ? "fr-FR" : language === "es" ? "es-ES" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/\u00a0/g, " ");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-list-page">
      <Navbar isScrolled={true} />

      <main className="wrap-cayus pt-28 pb-16 sm:pb-20">
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1B1B1B] tracking-tight mb-3"
          data-testid="blog-page-title"
        >
          {t.blog}
        </h1>
        <div className="w-16 h-1 bg-[#0047AB] rounded-full mb-12" />

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D9D9D9] border-t-[#0047AB] rounded-full animate-spin" />
          </div>
        ) : articles.length === 0 ? (
          <p className="text-[#5E6C83] text-center py-20" data-testid="blog-no-posts">
            {t.noPosts}
          </p>
        ) : (
          <div className={`grid gap-8 ${articles.length < 3 ? "sm:grid-cols-2 max-w-3xl" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="blog-articles-grid">
            {articles.map((article) => {
              const title = getField(article, "title");
              const content = getField(article, "content");
              const excerpt = stripHtml(content).substring(0, 150);
              return (
                <Link
                  key={article.id}
                  to={`/blog/${article.slug}`}
                  className="group block bg-white border border-[#D9D9D9] rounded-2xl overflow-hidden hover:shadow-lg hover:border-[#C3CFE0] transition-all duration-300"
                  data-testid={`blog-card-${article.slug}`}
                >
                  {article.image_url && (
                    <div className="aspect-[16/9] overflow-hidden bg-[#F3F3F3]">
                      <img
                        src={article.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${article.image_url}` : article.image_url}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.onerror = null; e.target.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-[#9098A3] mb-3">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(article.created_at)}
                    </div>
                    <h2 className="font-bold text-lg text-[#1B1B1B] mb-2 group-hover:text-[#0047AB] transition-colors line-clamp-2">
                      {title}
                    </h2>
                    <p className="text-sm text-[#5E6C83] line-clamp-3 mb-4">
                      {excerpt}...
                    </p>
                    <span className="text-sm font-medium text-[#0047AB] flex items-center gap-1">
                      {t.readMore}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogListPage;
